// src/contexts/BudgetContext.js
// client/src/contexts/BudgetContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';
import { useDateFilter } from './DateFilterContext';
import axios from 'axios';

// API configuration - ALWAYS use the deployed backend in production
// const API_BASE_URL = process.env.NODE_ENV === 'production'
//   ? (process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api')
//   : 'http://localhost:3001/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

console.log('API BASE URL:', API_BASE_URL);

// Create the budget context with meaningful default values
const BudgetContext = createContext({
    // Data collections
    budgets: [],
    categories: [],
    transactions: [],
    
    // Financial metrics
    totalIncome: 0,
    totalExpenses: 0,
    monthlyBudget: 0,
    
    // State indicators
    loading: false,
    error: null,
    
    // Management functions
    handleSaveBudget: async () => {},
    handleDeleteBudget: async () => {},
    handleSaveCategory: async () => {},
    handleDeleteCategory: async () => {},
    handleSaveTransaction: async () => {},
    handleDeleteTransaction: async () => {},
    handleSaveCategoryBudget: async () => {},
});

// Helper function to create authenticated API instance
const createAuthApi = () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
        throw new Error('No authentication token found');
    }
    
    return axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
        }
    });
};

// Helper to convert MongoDB transactions format to app format
const formatTransaction = (transaction) => {
    return {
        id: transaction._id,
        amount: transaction.amount,
        description: transaction.description,
        date: transaction.date,
        category: transaction.category,
        categoryId: transaction.categoryId,
        type: transaction.type || 'expense',
        budgetId: transaction.budgetId,
        budgetMonth: transaction.budgetMonth, // Preserve budgetMonth field
        paymentMethod: transaction.paymentMethod || 'other',
        tags: transaction.tags || [],
        recurring: transaction.recurring || false,
        recurringConfig: transaction.recurringConfig, // Preserve recurring configuration
        isRecurringInstance: transaction.isRecurringInstance || false, // Mark auto-created instances
        createdAt: transaction.createdAt || new Date().toISOString(),
        deviceId: transaction.deviceId || navigator.userAgent // Track device info
    };
};

// Function to enhance transactions with category information
const enhanceTransactionsWithCategoryInfo = (transactions, categories) => {
    return transactions.map(tx => {
        // Find matching category
        const category = categories.find(c => 
            c.name === tx.category || 
            c.id === tx.categoryId || 
            String(c.id) === String(tx.category)
        );
        
        return {
            ...tx,
            categoryName: category ? category.name : (tx.category || 'Uncategorized'),
            categoryColor: category ? category.color : '#9c27b0', // Default purple
            categoryIcon: category ? category.icon : '📊'
        };
    });
};

// Helper to convert MongoDB budget format to app format
const formatBudget = (budget) => {
    return {
        id: budget._id,
        title: budget.title,
        amount: budget.amount,
        spent: budget.spent || 0,
        category: budget.category,
        color: budget.color,
        period: budget.period,
        userId: budget.owner,
        createdAt: budget.createdAt || new Date().toISOString()
    };
};

// Helper to convert MongoDB category format to app format
const formatCategory = (category, userId) => {
    // Initialize monthlyBudgets - only use server data, no fallbacks for true month independence
    let monthlyBudgets = {};
    
    if (category.monthlyBudgets && typeof category.monthlyBudgets === 'object') {
        // Server has monthly budgets structure - convert Map to object if needed
        if (category.monthlyBudgets instanceof Map) {
            monthlyBudgets = Object.fromEntries(category.monthlyBudgets);
        } else {
            monthlyBudgets = category.monthlyBudgets;
        }
    }
    // No fallback - if no monthlyBudgets exist, start with empty object
    
    // Initialize rolloverData - preserve rollover info from server
    let rolloverData = {};
    if (category.rolloverData && typeof category.rolloverData === 'object') {
        if (category.rolloverData instanceof Map) {
            rolloverData = Object.fromEntries(category.rolloverData);
        } else {
            rolloverData = category.rolloverData;
        }
    }
    
    return {
        id: category._id,
        name: category.name,
        type: category.type || 'expense',
        color: category.color || '#7F8C8D',
        icon: category.icon,
        isDefault: category.isDefault || false,
        budget: Number(category.budget || 0), // Keep legacy budget field for compatibility
        monthlyBudgets: monthlyBudgets, // True month-specific budgets
        rolloverEnabled: category.rolloverEnabled !== undefined ? category.rolloverEnabled : true,
        rolloverType: category.rolloverType || 'one-month',
        rolloverCap: category.rolloverCap,
        rolloverData: rolloverData,
        rolloverHistory: category.rolloverHistory || [],
        isShared: category.isShared || false,
        sharedCategoryId: category.sharedCategoryId || null,
        fundingAccountId: category.fundingAccountId || null,
        userId: userId
    };
};

export const BudgetProvider = ({ children }) => {
    // Get the authenticated user from our auth context
    const { user } = useAuth();
    
    // Get date filter values for month-aware calculations
    const { 
        selectedMonth, 
        selectedYear, 
        effectiveStartDate, 
        effectiveEndDate,
        filterMode 
    } = useDateFilter();

    // State management for our financial data
    const [budgets, setBudgets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [monthlyBalance, setMonthlyBalance] = useState({ opening: 0, closing: 0, income: 0, expenses: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Track sync status
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    
    // Device identifier
    const [deviceId] = useState(() => {
        return localStorage.getItem('deviceId') || `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    });

    useEffect(() => {
        // Store device ID for tracking
        localStorage.setItem('deviceId', deviceId);
    }, [deviceId]);

    // Privacy guard: drop ALL cached financial data the moment the
    // authenticated user changes (logout, account swap, or 401-forced
    // logout). Without this, User A's budgets / categories / transactions
    // remain in React state after User A logs out and would be visible
    // to User B if they log in on the same tab before each context
    // re-fetches. See client/src/hooks/useAuthReset.js.
    const resetBudgetState = useCallback(() => {
        setBudgets([]);
        setCategories([]);
        setTransactions([]);
        setMonthlyBalance({ opening: 0, closing: 0, income: 0, expenses: 0 });
        setError(null);
        setConnectionError(null);
        setLastSyncTime(null);
    }, []);
    useAuthReset(resetBudgetState);
  
    // Function to refresh transaction category info
    const refreshTransactionsWithCategoryInfo = (cats) => {
        const catsList = cats || categories;
        if (transactions.length > 0 && catsList.length > 0) {
            const updatedTransactions = enhanceTransactionsWithCategoryInfo(transactions, catsList);
            setTransactions(updatedTransactions);
        }
    };

    // Modify the handleSaveCategory function - ALWAYS save to MongoDB
    const handleSaveCategory = async (categoryData) => {
        console.log('🔄 BudgetContext.handleSaveCategory called with:', categoryData);
        
        if (!user?.id) {
            console.error('❌ No authenticated user');
            throw new Error('No authenticated user');
        }
      
        try {
            setLoading(true);
            setSyncing(true);
            console.log('📤 Starting category save process...');
            
            // Get current month budget from monthlyBudgets structure
            const now = new Date();
            const currentMonthKey = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
            console.log('🗓️ Current month key:', currentMonthKey);
            
            // Preserve all existing monthly budgets and only update specific months
            let monthlyBudgetsToSave = {};
            if (categoryData.monthlyBudgets && typeof categoryData.monthlyBudgets === 'object') {
                monthlyBudgetsToSave = { ...categoryData.monthlyBudgets };
                console.log('📅 Using provided monthly budgets:', monthlyBudgetsToSave);
            }
            
            // Determine the budget to save for legacy field (use current month or fallback)
            let legacyBudgetToSave = 0;
            if (monthlyBudgetsToSave[currentMonthKey] !== undefined) {
                legacyBudgetToSave = Number(monthlyBudgetsToSave[currentMonthKey]);
                console.log('💰 Using current month budget for legacy field:', legacyBudgetToSave);
            } else if (categoryData.budget !== undefined) {
                legacyBudgetToSave = Number(categoryData.budget);
                // If budget was set but no monthly structure exists, create it for current month
                monthlyBudgetsToSave[currentMonthKey] = legacyBudgetToSave;
                console.log('💰 Using provided budget for legacy field and current month:', legacyBudgetToSave);
            }
          
            // Check if this is a MongoDB ID or a local ID
            const isMongoId = categoryData.id && 
                            /^[0-9a-fA-F]{24}$/.test(categoryData.id.toString());
            console.log('🆔 Is MongoDB ID:', isMongoId, 'ID:', categoryData.id);
          
            try {
                const api = createAuthApi();
                
                // Format category for MongoDB - preserve all monthly budgets
                const mongoCategory = {
                    name: categoryData.name,
                    type: categoryData.type || 'expense',
                    color: categoryData.color || '#7F8C8D',
                    icon: categoryData.icon,
                    budget: parseFloat(legacyBudgetToSave) || 0, // Legacy field for compatibility
                    monthlyBudgets: monthlyBudgetsToSave, // Save complete monthly structure
                    fundingAccountId: categoryData.fundingAccountId || null,
                    deviceId: deviceId // Add device identifier
                };
                
                console.log('📝 Saving category with data:', mongoCategory);
                console.log('📅 Monthly budgets to save:', monthlyBudgetsToSave);
                console.log('💰 Legacy budget field:', legacyBudgetToSave);
                
                let response;
                
                if (isMongoId) {
                    // Update existing category
                    console.log(`Updating MongoDB category with ID: ${categoryData.id}`);
                    console.log('Sending update data:', JSON.stringify(mongoCategory));
                    response = await api.put(`/user-preferences/categories/${categoryData.id}`, mongoCategory);
                    console.log('Category update response:', response.data);
                } else {
                    // Create new category
                    console.log('Creating new category in MongoDB with data:', mongoCategory);
                    response = await api.post('/user-preferences/categories', mongoCategory);
                    console.log('Category created:', response.data);
                }
                
                // Get updated categories
                const categoriesResponse = await api.get('/user-preferences/categories');
                
                // Convert to app format
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                
                console.log('📥 Raw categories from server:', categoriesData.length, 'items');
                console.log('📊 Sample category data:', categoriesData[0]);
                
                const formattedCategories = categoriesData.map(cat => 
                    formatCategory(cat, user.id)
                );
                
                console.log('✨ Formatted categories:', formattedCategories.length, 'items');
                console.log('🎯 Sample formatted category:', formattedCategories[0]);
                
                setCategories(formattedCategories);
                console.log('✅ Categories updated in state from MongoDB');
                
                // After updating categories, refresh transactions to ensure
                // they have the proper category information
                refreshTransactionsWithCategoryInfo(formattedCategories);
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                console.log('🎉 Category save completed successfully');
                
            } catch (error) {
                console.error('Error saving category to MongoDB:', error);
                setConnectionError(`Failed to save category: ${error.message}`);
                throw error;
            }
        } catch (err) {
            setError('Failed to save category: ' + err.message);
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    // Category budget update function
    const handleSaveCategoryBudget = async (categoryId, budgetAmount) => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }
      
        try {
            setLoading(true);
            setSyncing(true);
          
            // Convert to number
            const budgetValue = (Number(budgetAmount) * 1);
            
            try {
                const api = createAuthApi();
                
                // Send direct budget update to server
                const response = await api.patch(`/user-preferences/categories/${categoryId}/budget-only`, {
                    budget: parseFloat(budgetValue)
                });
                
                console.log('Budget update response:', response.data);
                
                // Get updated categories
                const categoriesResponse = await api.get('/user-preferences/categories');
                
                // Process and update categories
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                
                const formattedCategories = categoriesData.map(cat => 
                    formatCategory(cat, user.id)
                );
                
                setCategories(formattedCategories);
                
                // Update transactions with new category info
                refreshTransactionsWithCategoryInfo(formattedCategories);
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                
                // AUTO-TRIGGER: Calculate rollover for the CURRENT month
                // This ensures rollover from previous month is applied when budget is set
                try {
                    const now = new Date();
                    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    
                    console.log('🔁 Auto-calculating rollover for current month:', currentMonthKey);
                    await calculateRollover(currentMonthKey);
                } catch (rolloverError) {
                    console.warn('Could not auto-calculate rollover:', rolloverError);
                    // Don't throw - rollover calculation failure shouldn't break budget update
                }
                
            } catch (error) {
                console.error('Budget update failed:', error);
                setConnectionError(`Failed to update budget: ${error.message}`);
                throw error;
            }
        } catch (err) {
            setError('Failed to save category budget: ' + err.message);
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    // Load user's financial data when they log in
    useEffect(() => {
        const loadUserData = async () => {
            if (!user?.id) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setSyncing(true);
                setError(null);

                try {
                    console.log('Loading data from MongoDB...');
                    const api = createAuthApi();
                    
                    // Fetch categories first
                    try {
                        console.log('Fetching categories...');
                        const categoriesResponse = await api.get('/user-preferences/categories');
                        
                        // Process categories
                        const categoriesData = Array.isArray(categoriesResponse.data) 
                            ? categoriesResponse.data 
                            : (categoriesResponse.data.categories || []);
                        
                        // Convert to app format
                        const formattedCategories = categoriesData.map(cat => 
                            formatCategory(cat, user.id)
                        );
                        setCategories(formattedCategories);
                        console.log('Categories loaded:', formattedCategories.length);
                        
                        // Fetch budgets
                        try {
                            console.log('Fetching budgets...');
                            const budgetsResponse = await api.get('/budgets');
                            
                            // Process budgets
                            const mongoBudgets = Array.isArray(budgetsResponse.data) 
                                ? budgetsResponse.data 
                                : (budgetsResponse.data.data || []);
                            
                            // Convert to app format
                            const formattedBudgets = mongoBudgets.map(formatBudget);
                            setBudgets(formattedBudgets);
                            console.log('Budgets loaded:', formattedBudgets.length);
                            
                            // Fetch transactions
                            try {
                                console.log('Fetching transactions...');
                                const transactionsResponse = await api.get('/transactions');
                                
                                // Process transactions
                                const transactionsData = Array.isArray(transactionsResponse.data)
                                    ? transactionsResponse.data
                                    : (transactionsResponse.data.transactions || []);
                                
                                console.log('Transactions data from API:', transactionsData);
                                
                                // Format and enhance transactions
                                const formattedTransactions = transactionsData.map(formatTransaction);
                                const enhancedTransactions = enhanceTransactionsWithCategoryInfo(
                                    formattedTransactions, formattedCategories
                                );
                                
                                setTransactions(enhancedTransactions);
                                console.log('Transactions loaded:', enhancedTransactions.length);
                                
                                // Update sync status
                                setLastSyncTime(new Date());
                                setConnectionError(null);
                                
                                // AUTO-TRIGGER: Enable rollover + calculate for current month on load
                                try {
                                    console.log('🔁 Auto-calculating rollover on load...');
                                    // Enable rollover for categories + calculate last 2 months
                                    await api.post('/budget-rollover/enable-all');
                                    
                                    // Re-fetch categories to include fresh rollover data
                                    const refreshedCats = await api.get('/user-preferences/categories');
                                    const refreshedData = Array.isArray(refreshedCats.data) 
                                        ? refreshedCats.data 
                                        : (refreshedCats.data.categories || []);
                                    const refreshedFormatted = refreshedData.map(cat => formatCategory(cat, user.id));
                                    setCategories(refreshedFormatted);
                                    console.log('✅ Rollover data refreshed');
                                } catch (rolloverErr) {
                                    console.warn('Could not auto-calculate rollover on load:', rolloverErr.message);
                                }
                                
                            } catch (transactionsError) {
                                console.error('Failed to load transactions:', transactionsError);
                                setConnectionError(`Failed to load transactions: ${transactionsError.message}`);
                                throw transactionsError;
                            }
                            
                        } catch (budgetError) {
                            console.error('Failed to load budgets:', budgetError);
                            setConnectionError(`Failed to load budgets: ${budgetError.message}`);
                            throw budgetError;
                        }
                        
                    } catch (categoryError) {
                        console.error('Failed to load categories:', categoryError);
                        setConnectionError(`Failed to load categories: ${categoryError.message}`);
                        throw categoryError;
                    }
                    
                    console.log('Data loaded successfully from MongoDB');

                } catch (apiError) {
                    console.error('API error loading data:', apiError);
                    setConnectionError(`Connection error: ${apiError.message}`);
                    throw apiError;
                }
            } catch (err) {
                console.error('Error loading user data:', err);
                setError(`Failed to load your financial data: ${err.message}`);
            } finally {
                setLoading(false);
                setSyncing(false);
            }
        };

        loadUserData();
    }, [user]);

    // Fetch monthly balance when selected month/year changes
    useEffect(() => {
        const fetchMonthBalance = async () => {
            if (!user?.id) return;
            
            try {
                const year = selectedYear;
                const month = String(selectedMonth + 1).padStart(2, '0');
                const monthKey = `${year}-${month}`;
                
                const api = createAuthApi();
                const response = await api.get(`/month-balance/${monthKey}`);
                
                if (response.data) {
                    setMonthlyBalance({
                        opening: response.data.opening || 0,
                        closing: response.data.closing || 0,
                        income: response.data.income || 0,
                        expenses: response.data.expenses || 0
                    });
                }
            } catch (error) {
                console.error('Error fetching month balance:', error);
                // Set to zeros on error - don't break the app
                setMonthlyBalance({ opening: 0, closing: 0, income: 0, expenses: 0 });
            }
        };

        fetchMonthBalance();
    }, [user, selectedMonth, selectedYear]);

    // Calculate derived financial metrics with month-aware budget logic
    const financialMetrics = useMemo(() => {
        // Helper function to get current month key from selectedMonth and selectedYear
        const getSelectedMonthKey = () => {
            // Convert zero-based month (0-11) to 1-based month (01-12) with padding
            const monthNumber = selectedMonth + 1;
            const monthString = monthNumber.toString().padStart(2, '0');
            return `${selectedYear}-${monthString}`;
        };

        // Helper function to get month-specific budget for a category
        const getMonthlyBudget = (category, monthKey = getSelectedMonthKey()) => {
            // Check if monthlyBudgets structure exists and is valid
            if (category.monthlyBudgets && typeof category.monthlyBudgets === 'object') {
                // Check if the specific month exists in monthlyBudgets
                const monthlyAmount = category.monthlyBudgets[monthKey];
                
                if (monthlyAmount !== undefined && monthlyAmount !== null) {
                    return Number(monthlyAmount);
                }
                
                // If monthlyBudgets exists but this specific month doesn't exist,
                // return 0 (true month independence - new months start with no budget)
                return 0;
            }
            
            // Fallback logic: ONLY for categories that don't have monthlyBudgets structure yet
            // This handles existing categories that haven't been migrated to monthly budgets
            const legacyBudget = Number(category.budget || 0);
            
            if (legacyBudget > 0) {
                return legacyBudget;
            }
            
            return 0;
        };

        // Get the selected month key for filtering transactions
        const selectedMonthKey = getSelectedMonthKey();

        // UPDATED: Filter transactions by effective date range (supports both month and range modes)
        const filterByDateRange = (transaction) => {
            if (!transaction.date) return false;
            
            const transactionDate = new Date(transaction.date);
            if (isNaN(transactionDate.getTime())) return false;
            
            // In month mode or when we have valid start/end dates
            if (effectiveStartDate && transactionDate < effectiveStartDate) return false;
            if (effectiveEndDate && transactionDate > effectiveEndDate) return false;
            
            return true;
        };

        const income = transactions
            .filter(t => t.type === 'income' && filterByDateRange(t))
            .reduce((sum, t) => sum + Number(t.amount || 0), 0);

        const expenses = transactions
            .filter(t => t.type === 'expense' && filterByDateRange(t))
            .reduce((sum, t) => sum + Number(t.amount || 0), 0);

        // Calculate monthly budget using month-aware logic synchronized with date filter
        // NOTE: For range mode, we still show the monthly budget for reference
        const monthlyBudget = categories
            .reduce((sum, category) => sum + getMonthlyBudget(category), 0);

        return {
            totalIncome: income,
            totalExpenses: expenses,
            monthlyBudget,
            openingBalance: monthlyBalance.opening,
            closingBalance: monthlyBalance.closing
        };
    }, [transactions, categories, selectedMonth, selectedYear, monthlyBalance, effectiveStartDate, effectiveEndDate, filterMode]);

    // Budget management functions
    const handleSaveBudget = async (budgetData, { silent = false } = {}) => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }

        try {
            setLoading(true);
            setSyncing(true);
            
            try {
                const api = createAuthApi();
                
                // Format budget for MongoDB
                const mongoBudget = {
                    title: budgetData.title,
                    amount: Number(budgetData.amount) || 0,
                    category: budgetData.category || 'Uncategorized',
                    color: budgetData.color || '#3498db',
                    period: budgetData.period || 'monthly',
                    deviceId: deviceId // Add device identifier
                };
                
                // Check if this is a MongoDB ID
                const isMongoId = budgetData.id && !budgetData.id.toString().includes('_') && 
                                 /^[0-9a-fA-F]{24}$/.test(budgetData.id.toString());
                
                if (isMongoId) {
                    // Update existing budget
                    await api.put(`/budgets/${budgetData.id}`, mongoBudget);
                } else {
                    // Create new budget
                    await api.post('/budgets', mongoBudget);
                }
                
                // Get updated budgets list
                const budgetsResponse = await api.get('/budgets');
                const mongoBudgets = Array.isArray(budgetsResponse.data) 
                    ? budgetsResponse.data 
                    : (budgetsResponse.data.data || []);
                
                // Convert MongoDB format to app format
                const formattedBudgets = mongoBudgets.map(formatBudget);
                setBudgets(formattedBudgets);
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                
            } catch (apiError) {
                console.error('API error saving budget:', apiError);
                setConnectionError(`Failed to save budget: ${apiError.message}`);
                throw apiError;
            }
        } catch (err) {
            // Only set the dashboard-level error when not in silent mode.
            // During onboarding the wizard uses silent=true so a failed budget
            // save shows only the banner, not the full dashboard error page.
            if (!silent) {
                setError('Failed to save budget: ' + err.message);
            }
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    const handleDeleteBudget = async (budgetId) => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }

        try {
            setLoading(true);
            setSyncing(true);
            
            try {
                const api = createAuthApi();
                
                // Delete budget
                await api.delete(`/budgets/${budgetId}`);
                
                // Update local state by removing the deleted budget
                setBudgets(prev => prev.filter(b => b.id !== budgetId));
                
                // Remove associated transactions from local state
                setTransactions(prev => prev.filter(t => t.budgetId !== budgetId));
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                
            } catch (apiError) {
                console.error('API error deleting budget:', apiError);
                setConnectionError(`Failed to delete budget: ${apiError.message}`);
                throw apiError;
            }
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }

        try {
            setLoading(true);
            setSyncing(true);
            console.log("Deleting category:", categoryId);
            
            try {
                const api = createAuthApi();
                
                // Delete category
                await api.delete(`/user-preferences/categories/${categoryId}?reassign=true`);
                
                // Get updated categories
                const categoriesResponse = await api.get('/user-preferences/categories');
                
                // Convert to app format
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                
                const formattedCategories = categoriesData.map(cat => 
                    formatCategory(cat, user.id)
                );
                
                setCategories(formattedCategories);
                console.log('Updated categories after delete:', formattedCategories);
                
                // Update transactions with new category info
                refreshTransactionsWithCategoryInfo(formattedCategories);
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                
            } catch (apiError) {
                console.error('API error deleting category:', apiError);
                setConnectionError(`Failed to delete category: ${apiError.message}`);
                throw apiError;
            }
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    // handleSaveTransaction function - ALWAYS save to MongoDB
    const handleSaveTransaction = async (transactionData) => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }
    
        try {
            setLoading(true);
            setSyncing(true);
            console.log('Saving transaction:', transactionData);
            console.log('Device ID:', deviceId);
            
            // Create a copy of the transaction data to work with
            let preparedTransaction = { ...transactionData };
            
            // Make sure to associate with category properly for expense transactions
            if (preparedTransaction.type === 'expense') {
                if (!preparedTransaction.category && preparedTransaction.categoryId) {
                    // Look up the category name from the ID
                    const category = categories.find(c => c.id === preparedTransaction.categoryId);
                    if (category) {
                        preparedTransaction.category = category.name;
                    }
                } else if (preparedTransaction.category && !preparedTransaction.categoryId) {
                    // Look up the category ID from the name
                    const category = categories.find(c => c.name === preparedTransaction.category);
                    if (category) {
                        preparedTransaction.categoryId = category.id;
                    }
                }
            } else if (preparedTransaction.type === 'income') {
                // For income transactions, completely remove the categoryId field
                delete preparedTransaction.categoryId;
                delete preparedTransaction.category;
            }
            
            try {
                // If not specified, use default budget
                if (!preparedTransaction.budgetId && budgets.length > 0) {
                    preparedTransaction.budgetId = budgets[0].id;
                }
                
                const api = createAuthApi();
                
                // Format transaction for MongoDB
                const mongoTransaction = {
                    description: preparedTransaction.description,
                    amount: Number(preparedTransaction.amount) || 0,
                    date: preparedTransaction.date || new Date().toISOString(),
                    type: preparedTransaction.type || 'expense',
                    paymentMethod: preparedTransaction.paymentMethod || 'other',
                    tags: preparedTransaction.tags || [],
                    recurring: preparedTransaction.recurring || false,
                    deviceId: deviceId // Add device identifier
                };
                
                // Include budgetMonth if provided
                if (preparedTransaction.budgetMonth) {
                    mongoTransaction.budgetMonth = preparedTransaction.budgetMonth;
                }
                
                // Include recurringConfig if this is a recurring transaction
                if (preparedTransaction.recurring && preparedTransaction.recurringConfig) {
                    mongoTransaction.recurringConfig = preparedTransaction.recurringConfig;
                }
                
                // Only add categoryId for expense transactions
                if (preparedTransaction.type === 'expense' && preparedTransaction.categoryId) {
                    mongoTransaction.categoryId = preparedTransaction.categoryId;
                    mongoTransaction.category = preparedTransaction.category;
                }
                
                // Only add budgetId if it exists
                if (preparedTransaction.budgetId) {
                    mongoTransaction.budgetId = preparedTransaction.budgetId;
                }

                // Include accountId when the transaction is tagged to an account
                if (preparedTransaction.accountId) {
                    mongoTransaction.accountId = preparedTransaction.accountId;
                }
                
                console.log('Saving transaction to MongoDB:', mongoTransaction);
                
                let response;
                
                // Check if this is a MongoDB ID
                const isMongoId = preparedTransaction.id && !preparedTransaction.id.toString().includes('_') && 
                                 /^[0-9a-fA-F]{24}$/.test(preparedTransaction.id.toString());
                
                if (isMongoId) {
                    // Update existing transaction using standalone endpoint
                    response = await api.put(`/transactions/${preparedTransaction.id}`, mongoTransaction);
                    console.log('Transaction updated successfully:', response.data);
                } else {
                    // Create new transaction using standalone endpoint
                    response = await api.post('/transactions', mongoTransaction);
                    console.log('Transaction created successfully:', response.data);
                }
                
                // Fetch all transactions to refresh the list
                const transactionsResponse = await api.get('/transactions');
                const transactionsData = Array.isArray(transactionsResponse.data)
                    ? transactionsResponse.data
                    : (transactionsResponse.data.transactions || []);
                
                // Format transactions
                const formattedTransactions = transactionsData.map(formatTransaction);
                
                // Enhance with category info
                const enhancedTransactions = enhanceTransactionsWithCategoryInfo(
                    formattedTransactions, categories
                );
                
                setTransactions(enhancedTransactions);
                console.log('Transactions refreshed from MongoDB:', enhancedTransactions.length);

                // Signal AccountContext to re-fetch balances (decoupled via event)
                if (mongoTransaction.accountId) {
                    window.dispatchEvent(new Event('soldikeeper:accounts-refresh'));
                }
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                
            } catch (apiError) {
                console.error('API error saving transaction:', apiError);
                
                // Handle subscription errors gracefully
                if (apiError.response?.status === 403 || 
                    apiError.message?.includes('subscription') ||
                    apiError.message?.includes('No subscription found')) {
                    
                    console.warn('Subscription error detected, providing user-friendly message:', apiError.message);
                    setConnectionError('Receipt processing completed successfully, but transaction saving requires a subscription. Please upgrade your account to save transactions automatically.');
                    
                    // Don't throw the error for subscription issues - let the receipt processing complete
                    return;
                } else {
                    setConnectionError(`Failed to save transaction: ${apiError.message}`);
                    throw apiError;
                }
            }
        } catch (err) {
            setError('Failed to save transaction: ' + err.message);
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    // handleDeleteTransaction - ALWAYS delete from MongoDB
    const handleDeleteTransaction = async (transactionId) => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }

        try {
            setLoading(true);
            setSyncing(true);
            
            try {
                const api = createAuthApi();
                
                // Delete transaction directly using standalone endpoint
                await api.delete(`/transactions/${transactionId}`);
                console.log(`Transaction ${transactionId} deleted successfully`);
                
                // Update local state
                setTransactions(prev => prev.filter(t => t.id !== transactionId));
                
                // Refresh transactions list from the server for consistency
                const transactionsResponse = await api.get('/transactions');
                const transactionsData = Array.isArray(transactionsResponse.data)
                    ? transactionsResponse.data
                    : (transactionsResponse.data.transactions || []);
                
                // Format transactions
                const formattedTransactions = transactionsData.map(formatTransaction);
                
                // Enhance with category info
                const enhancedTransactions = enhanceTransactionsWithCategoryInfo(
                    formattedTransactions, categories
                );
                
                setTransactions(enhancedTransactions);
                console.log('Transactions refreshed after deletion');
                
                // Update sync status
                setLastSyncTime(new Date());
                setConnectionError(null);
                
            } catch (apiError) {
                console.error('API error deleting transaction:', apiError);
                setConnectionError(`Failed to delete transaction: ${apiError.message}`);
                throw apiError;
            }
        } catch (err) {
            setError('Failed to delete transaction: ' + err.message);
            throw err;
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    // Manual sync function to force refresh from server
    const syncWithServer = async () => {
        if (!user?.id) {
            throw new Error('No authenticated user');
        }

        try {
            setSyncing(true);
            setLoading(true);
            console.log('Manually syncing data with server...');
            
            const api = createAuthApi();
            
            // Fetch all data from server
            const [categoriesResponse, budgetsResponse, transactionsResponse] = await Promise.all([
                api.get('/user-preferences/categories'),
                api.get('/budgets'),
                api.get('/transactions')
            ]);
            
            // Process categories
            const categoriesData = Array.isArray(categoriesResponse.data) 
                ? categoriesResponse.data 
                : (categoriesResponse.data.categories || []);
            
            const formattedCategories = categoriesData.map(cat => formatCategory(cat, user.id));
            setCategories(formattedCategories);
            
            // Process budgets
            const budgetsData = Array.isArray(budgetsResponse.data) 
                ? budgetsResponse.data 
                : (budgetsResponse.data.data || []);
            
            const formattedBudgets = budgetsData.map(formatBudget);
            setBudgets(formattedBudgets);
            
            // Process transactions
            const transactionsData = Array.isArray(transactionsResponse.data)
                ? transactionsResponse.data
                : (transactionsResponse.data.transactions || []);
            
            const formattedTransactions = transactionsData.map(formatTransaction);
            const enhancedTransactions = enhanceTransactionsWithCategoryInfo(
                formattedTransactions, formattedCategories
            );
            
            setTransactions(enhancedTransactions);
            
            // Update sync status
            setLastSyncTime(new Date());
            setConnectionError(null);
            console.log('Manual sync completed successfully');
            
            return true;
        } catch (error) {
            console.error('Manual sync failed:', error);
            setConnectionError(`Sync failed: ${error.message}`);
            setError(`Failed to sync with server: ${error.message}`);
            return false;
        } finally {
            setSyncing(false);
            setLoading(false);
        }
    };

    // Budget Rollover Functions
    
    // Calculate rollover for all enabled categories
    const calculateRollover = async (monthKey) => {
        try {
            const api = createAuthApi();
            const response = await api.post('/budget-rollover/calculate', { monthKey });
            
            if (response.data.success) {
                // Refresh categories to get updated rollover data
                const categoriesResponse = await api.get('/user-preferences/categories');
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                const formattedCategories = categoriesData.map(cat => formatCategory(cat, user.id));
                setCategories(formattedCategories);
                
                return response.data;
            }
            throw new Error(response.data.message || 'Failed to calculate rollover');
        } catch (error) {
            console.error('Error calculating rollover:', error);
            setError(`Failed to calculate rollover: ${error.message}`);
            throw error;
        }
    };

    // Calculate rollover for specific category
    const calculateCategoryRollover = async (categoryName, monthKey) => {
        try {
            const api = createAuthApi();
            const response = await api.post(`/budget-rollover/calculate/${categoryName}`, { monthKey });
            
            if (response.data.success) {
                // Refresh categories to get updated rollover data
                const categoriesResponse = await api.get('/user-preferences/categories');
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                const formattedCategories = categoriesData.map(cat => formatCategory(cat, user.id));
                setCategories(formattedCategories);
                
                return response.data;
            }
            throw new Error(response.data.message || 'Failed to calculate category rollover');
        } catch (error) {
            console.error('Error calculating category rollover:', error);
            setError(`Failed to calculate category rollover: ${error.message}`);
            throw error;
        }
    };

    // Get rollover info for a category
    const getRolloverInfo = async (categoryName, monthKey) => {
        try {
            const api = createAuthApi();
            const response = await api.get(`/budget-rollover/info/${categoryName}/${monthKey}`);
            return response.data;
        } catch (error) {
            console.error('Error getting rollover info:', error);
            throw error;
        }
    };

    // Get overspent categories for a month
    const getOverspentCategories = async (monthKey) => {
        try {
            const api = createAuthApi();
            const response = await api.get(`/budget-rollover/overspent/${monthKey}`);
            return response.data.overspentCategories || [];
        } catch (error) {
            console.error('Error getting overspent categories:', error);
            return [];
        }
    };

    // Update rollover settings for a category
    const updateRolloverSettings = async (categoryName, settings) => {
        try {
            const api = createAuthApi();
            const response = await api.put(`/budget-rollover/settings/${categoryName}`, settings);
            
            if (response.data.success) {
                // Refresh categories to get updated settings
                const categoriesResponse = await api.get('/user-preferences/categories');
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                const formattedCategories = categoriesData.map(cat => formatCategory(cat, user.id));
                setCategories(formattedCategories);
                
                return response.data;
            }
            throw new Error(response.data.message || 'Failed to update rollover settings');
        } catch (error) {
            console.error('Error updating rollover settings:', error);
            setError(`Failed to update rollover settings: ${error.message}`);
            throw error;
        }
    };

    // Get rollover history for a category
    const getRolloverHistory = async (categoryName, months = 6) => {
        try {
            const api = createAuthApi();
            const response = await api.get(`/budget-rollover/history/${categoryName}?months=${months}`);
            return response.data;
        } catch (error) {
            console.error('Error getting rollover history:', error);
            throw error;
        }
    };

    // Auto-calculate rollover for current month
    const autoCalculateRollover = async () => {
        try {
            const api = createAuthApi();
            const response = await api.post('/budget-rollover/auto-calculate');
            
            if (response.data.success) {
                // Refresh categories to get updated rollover data
                const categoriesResponse = await api.get('/user-preferences/categories');
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                const formattedCategories = categoriesData.map(cat => formatCategory(cat, user.id));
                setCategories(formattedCategories);
                
                return response.data;
            }
            return response.data;
        } catch (error) {
            console.error('Error auto-calculating rollover:', error);
            return null;
        }
    };

    // Get debug budgets data
    const getDebugBudgets = async () => {
        try {
            const api = createAuthApi();
            const response = await api.get('/budget-rollover/debug-budgets');
            return response.data;
        } catch (error) {
            console.error('Error getting debug budgets:', error);
            throw error;
        }
    };

    // Dismiss or re-accept rollover for a category/month
    const dismissRollover = async (categoryName, monthKey, dismissed = true) => {
        try {
            const api = createAuthApi();
            const response = await api.post(
                `/budget-rollover/dismiss/${encodeURIComponent(categoryName)}/${monthKey}`,
                { dismissed }
            );
            
            if (response.data.success) {
                // Refresh categories to reflect the change
                const categoriesResponse = await api.get('/user-preferences/categories');
                const categoriesData = Array.isArray(categoriesResponse.data) 
                    ? categoriesResponse.data 
                    : (categoriesResponse.data.categories || []);
                const formattedCategories = categoriesData.map(cat => formatCategory(cat, user.id));
                setCategories(formattedCategories);
                return response.data;
            }
            throw new Error(response.data.message || 'Failed to dismiss rollover');
        } catch (error) {
            console.error('Error dismissing rollover:', error);
            setError(`Failed to dismiss rollover: ${error.message}`);
            throw error;
        }
    };

    // Function to get category information for a transaction
    const getCategoryInfo = (categoryIdentifier) => {
        if (!categoryIdentifier) return { name: 'Uncategorized', color: '#9c27b0', icon: '📊' };
        
        const category = categories.find(c => 
            c.id === categoryIdentifier || 
            c.name === categoryIdentifier
        );
        
        return category || { name: categoryIdentifier, color: '#9c27b0', icon: '📊' };
    };

    // Create the context value object with all our data and functions
    const value = {
        // Data collections
        budgets,
        categories,
        transactions,
        monthlyBalance,
        
        // Financial metrics
        ...financialMetrics,
        
        // State indicators
        loading,
        error,
        syncing,
        lastSyncTime,
        connectionError,
        deviceId,
        
        // Management functions
        handleSaveBudget,
        handleDeleteBudget,
        handleSaveCategory,
        handleDeleteCategory,
        handleSaveTransaction,
        handleDeleteTransaction,
        handleSaveCategoryBudget,
        
        // Sync function
        syncWithServer,
        clearErrors: () => { setError(null); setConnectionError(null); },
        
        // Rollover functions
        calculateRollover,
        calculateCategoryRollover,
        getRolloverInfo,
        getOverspentCategories,
        updateRolloverSettings,
        getRolloverHistory,
        autoCalculateRollover,
        getDebugBudgets,
        dismissRollover,
        // Utility functions
        getCategoryInfo
    };

    // Finally, provide the context value to all child components
    return (
        <BudgetContext.Provider value={value}>
            {connectionError && (
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: '#f44336',
                    color: 'white',
                    padding: '8px 16px',
                    textAlign: 'center',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span>{connectionError}</span>
                    <button 
                        onClick={syncWithServer}
                        style={{
                            background: 'white',
                            color: '#f44336',
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}
            {syncing && (
                <div style={{
                    position: 'fixed',
                    top: '10px',
                    right: '10px',
                    background: '#2196f3',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    zIndex: 9999
                }}>
                    Syncing...
                </div>
            )}
            {children}
        </BudgetContext.Provider>
    );
};

// Custom hook for consuming the budget context
export const useBudget = () => {
    const context = useContext(BudgetContext);
    if (!context) {
        throw new Error('useBudget must be used within a BudgetProvider');
    }
    return context;
};

// Export both the provider and the hook
export default BudgetProvider;